import type { Messages } from '../messages.js'

/**
 * Swahili. `hatua` for flips, the way Dutch takes `zetten`: a move you spend, which is what the
 * meter counts, rather than the physical turning of a tile.
 *
 * `vigae` for tiles and `ubao` for the board. Several of the counted nouns here are class 9/10
 * and so identical in both numbers, which is why some plural forms look unchanged.
 */
export const sw: Messages = {
  tag: 'sw',

  readingDictionary: 'Kamusi inasomwa…',
  noWordList: 'Hakuna orodha ya maneno kwa "{language}". Tengeneza moja:  pnpm dictionary build',
  emptyWordList: 'Orodha ya maneno ya "{language}" haina kitu.',

  flips: 'hatua',
  score: 'alama',
  words: 'maneno',
  round: 'raundi',
  ticksLeftLabel: 'Muda uliobaki katika raundi hii',
  typeAWord: 'andika neno',
  tapPrompt: 'gusa herufi kuchagua au kurudisha, kisha {action}',

  boardOfTiles: 'Ubao wa vigae {n}',
  faceDown: 'kimefunikwa',
  wildCard: 'joker',
  wildKey: 'herufi yoyote',
  letterReplaced: '{from} imekuwa {to}',
  letterSwap: 'HERUFI IMEBADILIKA!',
  spentTile: 'kigae kilichotumika',
  hiddenWhilePaused: 'kimefichwa wakati mchezo umesimama',
  letterInWord: '{letter}, herufi ya {position} ya neno',

  completeWord: 'Kamilisha neno',

  completeShort: 'Kamilisha',
  reset: 'Futa',
  pause: 'Simamisha',
  resume: 'Endelea',
  newGame: 'Mchezo mpya',
  paused: 'Umesimama',
  outOfFlips: 'Hatua zimeisha',
  finalResult: 'alama {score} kutoka {words} katika {rounds}',
  playAgain: 'Cheza tena',
  share: 'Shiriki',
  shareCopied: 'Imenakiliwa.',
  shareSelect: 'Nakili hii:',

  lettersSelect: 'herufi huchagua',
  keysWild: 'huchukuliwa unapoandika herufi ambayo hakuna kigae kinachoionyesha',
  clearsEvery: 'huondoa herufi {letter} zote zilizochaguliwa',
  undoLastLetter: 'rudisha herufi ya mwisho',
  noWordsYet: 'Bado hakuna neno.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'limeshapatikana',
  reasonTooShort: 'fupi mno',
  reasonNotAWord: 'si neno',
  reasonAllFound: 'tayari unayo yote',
  noSuchLetterUp: 'hakuna {letter} wazi',
  nothingUp: 'hakijafunuliwa',
  letterHidden: 'herufi moja imejificha',
  shuffled: 'vimechanganywa',
  shuffledAndBilled: 'vimechanganywa, umetozwa hatua {flips} zisizotumika',
  tooFewLetters: 'Herufi zilizosalia ni chache mno',

  gameLanguage: 'lugha',
  interfaceLanguage: 'kiolesura',
  dictionarySize: 'maneno {common} ya kawaida kati ya {full}',
  filterLanguages: 'Tafuta lugha',
  noMatches: 'Hakuna matokeo',

  nerdMode: 'hali ya kitaalamu',
  rules: 'Kanuni',
  difficulty: 'ugumu',
  difficultyNames: { easy: 'rahisi', medium: 'wastani', hard: 'gumu', insane: 'kichaa' },
  pausedNoBoard: 'Mchezo huu hautaonekana kwenye jedwali la viongozi.',
  about: 'Kuhusu',
  themeLabel: 'mandhari',
  themeNames: { traditional: 'ya kawaida', light: 'angavu', contrast: 'utofautiano mkubwa' },
  tiles: 'vigae (N)',
  secondsPerTick: 'sekunde / mpigo',
  holdTicks: 'mipigo ya kusubiri',
  minWord: 'neno fupi zaidi',
  startingFlips: 'hatua za kuanzia',
  wildChance: 'nafasi ya joker',
  replaceChance: 'nafasi ya kubadilisha herufi',
  hideChance: 'nafasi ya herufi kujificha',
  wordCompleteMode: 'neno likikamilika',
  wordCompleteNames: { shuffle: 'changanya', spend: 'tumia', keep: 'weka' },
  flipEconomy: 'uchumi wa hatua',
  flipEconomyNames: {
    none: 'hakuna',
    perLetter: 'kwa kila herufi',
    fibonacci: 'fibonacci',
    overMinimum: 'zaidi ya kima',
  },
  repeatedLetterKey: 'kitufe cha herufi inayorudiwa',
  keySchemeNames: { cycle: 'mzunguko', advance: 'mbele' },
  keySchemeHelp: {
    cycle:
      'A huchukua A inayofuata ambayo haijatumika, na zikishaingia zote katika neno, huziondoa. ' +
      'Shift+A pia huziondoa.',
    advance: 'A huchukua A inayofuata ambayo haijatumika. Shift+A huondoa kila A katika neno.',
  },

  whatThatMeans: 'Maana yake',
  factRound: 'raundi',
  factWholeBoardUp: 'ubao mzima wazi kwa',
  factRoundCosts: 'raundi moja hugharimu',
  factFlipsBuy: 'hatua za kuanzia hununua',
  factThisBoard: 'ubao huu',
  factBoardHadToAdmit: 'ubao ulipaswa kukubali',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, refu zaidi {longest}',
  wordsIncludingOneOf: '{words} pamoja na moja la {ceiling}',
  scorelessRounds: '{rounds} bila alama',

  whatAWordPays: 'Neno hulipa nini',
  columnLetters: 'herufi',
  columnCost: 'gharama',
  columnPoints: 'alama',
  columnFlips: 'hatua',
  columnNet: 'baki',

  canonicalRules: 'Kanuni za kawaida za {difficulty}.',
  customRules: 'Umebadilisha kutoka mpangilio. Alama za kanuni zako mwenyewe hazipangwi.',
  applyAndStart: 'Tumia na uanze mchezo mpya',
  changesNextGame: 'Mabadiliko yataanza kutumika mchezo ujao.',
  presets: 'Mipangilio:',

  start: 'Anza',
  restart: 'Anza upya',
  quit: 'Toka',
  quitTitle: 'Utoke katika mchezo huu?',
  restartTitle: 'Uanze mchezo huu upya?',
  restartConfirm: 'Anza upya',
  quitConfirm: 'Toka',
  keepPlaying: 'Endelea kucheza',
  personalBest: 'Michezo yako bora',
  newPersonalBest: 'Rekodi mpya yako binafsi.',
  columnRank: '#',
  notRanked: 'Kanuni zako mwenyewe, kwa hivyo mchezo huu haupangwi.',
  notRankedPaused: 'Saa ilisimama, kwa hivyo mchezo huu haupangwi.',
  rankOfTotal: '{rank} kati ya {total}',

  howToPlay: 'Jinsi ya kucheza',

  backToGame: 'Rudi kwenye mchezo',
  welcomeTitle: 'Karibu Blinkered',
  tutorialSkip: 'Ruka',
  tutorialNext: 'Ifuatayo',
  tutorialBack: 'Nyuma',
  tutorialStart: 'Anza kucheza',
  tutorialHideAgain: 'Usionyeshe hii tena',
  tutorialProgress: '{n} kati ya {total}',
  tutorialSkipTitle: 'Uruke maelezo?',
  tutGoalTitle: 'Blinkered ni mbio',
  tutGoalBody:
    'Unaanza mchezo ukiwa na idadi maalum ya hatua, na kila kigae kinachofunuka hugharimu hatua moja. Hatua zikiisha, mchezo umekwisha. Maneno hukupa hatua, na kadiri neno linavyokuwa refu, ndivyo unavyopata hatua nyingi zaidi.',
  tutSpentBody:
    'Herufi unazotumia katika neno hufunikwa hadi raundi inayofuata. Ni ubadilishaji wa herufi pekee unaobadilisha herufi zilizo ubaoni.',
  tutChangeTitle: 'Herufi zinaweza kubadilika',
  tutSameLetters:
    'Umeona neno ambalo hukuwahi kulicheza? Herufi zilezile zinarudi raundi inayofuata, zikiwa zimechanganywa katika nafasi mpya.',
  tutPickLetters: 'Gusa herufi unazotaka, kwa mpangilio, ili kuunda neno.',
  tutMoreTurn: 'Vigae vinaendelea kufunuka unapofikiri, kwa hivyo herufi bora bado inaweza kuja.',
  tutTapBack:
    'Umegusa moja usiyoitaka? Igusa tena ili kuirudisha. Yoyote kati yake, si ya mwisho tu.',
  tutComplete: 'Bonyeza Kamilisha neno likiwa tayari.',
  htBoardTitle: 'Ubao',
  htBoardBody:
    'Vigae hufunuka kimoja baada ya kingine, kwa mpangilio wa nasibu. Kutoka vigae vilivyo wazi unaunda maneno.',
  htWordsTitle: 'Maneno',
  htWordsBody: 'Unda neno kutoka vigae vilivyo wazi kwa kuandika au kubofya herufi kwa mpangilio.',
  htFlipsTitle: 'Hatua',
  htFlipsBody:
    'Kila kigae kinachofunuka hugharimu hatua moja. Neno lililokamilika hurudisha hatua kwenye jumla yako, na maneno marefu hulipa zaidi. Hatua zikiisha, mchezo umekwisha.',
  htRoundTitle: 'Raundi',
  htRoundBody:
    'Kigae cha mwisho cha raundi kinapofunuka, ubao mzima hubaki wazi kwa muda mfupi. Kisha vigae hufunikwa na kuchanganywa, na raundi mpya huanza.',
  htLanguagesTitle: 'Lugha',
  htLanguagesBody:
    'Lugha {n}. Kila ubao unaweza kutatuliwa kwa maneno ambayo watu hutumia kweli. Neno lisilo la kawaida bado hupata alama, kama kamusi inalijua.',
  htKeysTitle: 'Kibodi',
  htWildTitle: 'Joker',
  htWildBody:
    'Wakati mwingine joker huonekana badala ya herufi. Joker huhesabiwa kama herufi yoyote inayounda neno sahihi. Neno ulilokwisha kamilisha halihesabiwi.',
  htHideTitle: 'Herufi zinazojificha',
  htHideBody:
    'Katika viwango vigumu zaidi, herufi iliyofunuka inaweza kujigeuza na kurudi baadaye katika raundi hiyo. Kipima muda hupata alama moja ya ziada kwa hilo, kwa rangi nyekundu. Herufi zilizochaguliwa hazijigeuzi.',
  tutHideBody:
    'Katika viwango vigumu zaidi, herufi iliyofunuka inaweza kujigeuza na kurudi baadaye katika raundi hiyo. Herufi zilizochaguliwa hazijigeuzi.',
  htSwapTitle: 'Herufi zinazobadilika',
  htSwapBody:
    'Wakati mwingine, kati ya raundi, herufi moja hubadilishwa na nyingine. Utaona ni herufi ipi imeondolewa na ipi imeongezwa.',
  htLevelsTitle: 'Viwango',
  htLevelEasy:
    'Herufi zile zile kumi na mbili mchezo mzima, kwa hivyo unaweza kuzijua na kubeba orodha ya maneno kichwani. Vigae hufunuka polepole, na ubao uliojaa hubaki wazi muda wa kutosha kumaliza kuchagua herufi zako.',
  htLevelMedium:
    'Mara kwa mara herufi moja hubadilika, kwa hivyo inakuwa vigumu zaidi kukumbuka maneno uliyokusudia kucheza baadaye. Muda mchache wa kuangalia, na mchache wa kufikiri.',
  htLevelHard:
    'Maneno ya herufi tatu hayahesabiwi tena, na herufi hubadilika kila raundi ya pili. Ubao unakuwa umefunuka kwa shida kabla ya kuchanganywa.',
  htLevelInsane:
    'Yote kwa pamoja, kwa kasi kamili. Ubao huchanganywa karibu mara tu baada ya hatua ya mwisho.',
  htTouchTitle: 'Skrini ya kugusa',
  htTouchBody:
    'Gusa kigae kilicho wazi ili kuchukua herufi yake. Gusa herufi yoyote uliyochukua ili kuirudisha. Kamilisha na Futa viko chini ya ubao.',

  accountTitle: 'Akaunti yako',
  menuProfile: 'Wasifu wangu',
  menuGames: 'Michezo yangu',
  menuPublicPage: 'Ukurasa wangu wa umma',
  menuSignedInAs: 'Umeingia kama',
  signIn: 'Ingia',
  signOut: 'Toka',
  signInTitle: 'Ingia au jisajili',
  signInLead: 'Akaunti huhifadhi michezo yako, kwenye kifaa chochote.',
  signInKeepGame: 'Ingia ili mchezo huu ubaki, na kila mmoja baada yake.',
  continueWithApple: 'Endelea na Apple',
  continueWithGoogle: 'Endelea na Google',
  signInOr: 'au',
  emailLabel: 'Barua pepe',
  codeLabel: 'Nambari',
  emailMeACode: 'Nitumieni nambari',
  working: 'Subiri kidogo…',
  sendNewCode: 'Tuma nambari mpya',
  codeSent: 'Angalia barua pepe yako. Nambari hudumu dakika 10.',
  notNow: 'Si sasa',
  badEmail: 'Hii haionekani kama anwani ya barua pepe.',
  badCode: 'Nambari hiyo haikufanya kazi. Nambari huisha baada ya dakika 10 na hutumika mara moja.',
  serverBusy: 'Hatukuweza kufikia seva. Jaribu tena baada ya muda kidogo.',
  serverDown: 'Hatukuweza kufikia seva.',
  offline: 'Nje ya mtandao',
  leaderboardTitle: 'Jedwali la Washindi',
  leaderboardEmpty: 'Hakuna michezo kwenye jedwali hili bado. Kuwa wa kwanza.',
  leaderboardWouldBe: 'Mchezo huu ungekuwa wapi',
  leaderboardThisGame: 'Mchezo huu',
  leaderboardRanksHere: 'Mchezo huu uko wapi',
  leaderboardOutside: 'Nje ya tano bora.',
  tutAccountTitle: 'Hifadhi alama zako',
  tutAccountBody:
    'Akaunti huhifadhi michezo yako, huweka alama zako kwenye jedwali, na hutunza wasifu wako.',
  ssoCancelled: 'Kuingia kumeghairiwa.',
  ssoExpired: 'Imechukua muda mrefu mno. Ingia tena.',
  ssoNoUsername: 'Hatukuweza kumaliza kutengeneza akaunti. Jaribu tena baada ya muda kidogo.',
  ssoFailed: 'Haikufanya kazi. Jaribu tena, au tumia barua pepe yako.',
  tabProfile: 'Wasifu',
  countryAny: 'Sipendi kusema',
  findCountry: 'Tafuta nchi',
  usernameLabel: 'Jina la mtumiaji',
  bioLabel: 'Kunihusu',
  countryLabel: 'Nchi',
  bioHint: 'Hakuna viungo. {left}/{max}',
  save: 'Hifadhi',
  saving: 'Inahifadhi…',
  saved: 'Imehifadhiwa.',
  nameTaken: 'Jina hilo tayari lina mtu.',
  nameTooShort: 'Refu kidogo zaidi, tafadhali.',
  nameTooLong: 'Ni refu mno.',
  nameBadCharacters: 'Herufi, tarakimu, vistari na vistari vya chini pekee.',
  nameBadEdges: 'Lazima lianze na kuishia kwa herufi au tarakimu.',
  nameMixedScripts: 'Chagua alfabeti moja na ubaki nayo.',
  nameReserved: 'Hili linafanana na jina tunalotoa sisi wenyewe. Chagua lingine.',
  nameUnusable: 'Jina hilo haliwezi kutumika.',
  bioTooLong: 'Hapa panatosha herufi {max} kwa wingi zaidi.',
  bioHasLink: 'Viungo haviruhusiwi hapa.',
  bioHasControl: 'Kuna herufi ambazo haziwezi kuwa hapa.',
  bioUnusable: 'Maandishi hayo hayawezi kutumika.',
  gamesHeading: 'Michezo',
  gamesEmpty: 'Bado hakuna kitu hapa. Michezo unayocheza ukiwa umeingia itaonekana hapa.',
  gamesEmptyShort: 'Bado hakuna kitu hapa.',
  gamesLoading: 'Inasoma michezo yako…',
  theirGamesLoading: 'Inasoma michezo yao…',
  profileLoading: 'Inasoma wasifu…',
  playerNotFound: 'Hakuna mtu hapa mwenye jina hilo.',
  columnWhen: 'Lini',
  columnRounds: 'raundi',
  columnGame: 'Mchezo',
  gameLoading: 'Inasoma mchezo…',
  gameUnreadable: 'Mchezo huu haukuweza kusomwa.',
  gameNotRanked: 'Umechezwa kwa kanuni zilizobadilishwa, kwa hiyo haupangwi.',
  detailNotKept: 'Maneno na mbao za mchezo huu hazihifadhiwi tena.',
  openingBoard: 'Ubao wa mwanzo',
  roundByRound: 'Raundi kwa raundi',
  roundNothing: 'hakuna kilichopatikana',
  changeReplaced: '{letter} imebadilishwa katika nafasi {slot}',
  changeWildOn: '{letter} joka katika nafasi {slot}',
  changeWildOff: '{letter} joka limeondoka nafasi {slot}',
  keepThisGame: 'Hifadhi mchezo huu',
  savedToYourGames: 'Imehifadhiwa kwenye michezo yako.',

  reportThis: 'Ripoti',
  reportHeading: 'Ripoti hii',
  reportTheName: 'Jina',
  reportTheBio: 'Wasifu',
  reportTheScore: 'Alama',
  reportWhy: 'Kuna nini kibaya nayo? Si lazima.',
  reportSend: 'Tuma ripoti',
  reportSent: 'Asante. Mtu ataiangalia.',
  reportSignIn: 'Ingia ili kuripoti kitu.',
  reportGone: 'Hiyo haipo tena.',
  reportCancel: 'Ghairi',

  deleteAccount: 'Futa akaunti yangu',
  deleteAccountWhat:
    'Hii inaondoa wasifu wako na kila mchezo uliohifadhi, kwa kudumu. Haiwezi kurudishwa.',
  deleteAccountAsk: 'Nitumie msimbo',
  deleteConfirm: 'Futa kwa kudumu',
  deleteGone: 'Akaunti yako haipo. Asante kwa kucheza.',
  deleteNoAddress:
    'Hakuna anwani iliyothibitishwa kwenye akaunti hii, kwa hivyo ufutaji hauwezi kuthibitishwa. Wasiliana nasi na tutafanya kwa mkono.',

  rulesetFacts: 'Maneno ya {letters} · kigae kipya kila {seconds}',
  plurals: {
    words: { one: 'neno {n}', other: 'maneno {n}' },
    rounds: { one: 'raundi {n}', other: 'raundi {n}' },
    flips: { one: 'hatua {n}', other: 'hatua {n}' },
    ticks: { one: 'mpigo {n}', other: 'mipigo {n}' },
    points: { one: 'alama {n}', other: 'alama {n}' },
    minLetters: { one: 'herufi {n} au zaidi', other: 'herufi {n} au zaidi' },
    seconds: { one: 'sekunde {n}', other: 'sekunde {n}' },
  },
}
